import { useRef, useEffect, useCallback } from 'react';
import { GRAPH_NODE_SIZE } from '@control-front-end/common/constants/graphActors';
import { EXPANDED_NODES_PADDING } from 'constants';

const HALF_NODE_SIZE = GRAPH_NODE_SIZE / 2;

/**
 * Calculate position for a clone node based on original node position and offsets
 */
const calculateClonePosition = (node, layerSettings) => {
  if (!node) return null;

  const position = node.position();
  const {
    left = 0,
    top = 0,
    right = 0,
    bottom = 0,
  } = layerSettings?.offset || {};

  return {
    x: position.x - (left - HALF_NODE_SIZE - right + HALF_NODE_SIZE) / 2,
    y:
      position.y +
      EXPANDED_NODES_PADDING -
      (top - HALF_NODE_SIZE - bottom + HALF_NODE_SIZE) / 2,
  };
};

/**
 * Hook to manage clone nodes for expanded actors
 * Creates and updates clone nodes with the same dimensions as expanded actors
 */
const useCloneExpNodes = ({ graph }) => {
  // Reference to track clone nodes, using the format: { originalNodeId: cloneNodeId }
  const cloneNodesRef = useRef({});

  /**
   * Helper function to create a clone node
   */
  const createCloneNodeInternal = (node) => {
    if (!graph) return;

    const data = node.data();

    // Check if the node is expanded
    const layerSettings = data.layerSettings || {};
    if (layerSettings.expand) {
      const existingCloneId = cloneNodesRef.current[data.id];

      if (!existingCloneId) {
        const cloneId = `clone-${data.id}`;

        // Calculate position with offsets for the clone node
        const position = calculateClonePosition(node, layerSettings);
        const {
          width,
          height,
          offset: { left = 0, right = 0, top = 0, bottom = 0 } = {},
        } = layerSettings;

        const layerSettingsSizes = {
          width: width || (left || 0) + (right || 0) + GRAPH_NODE_SIZE,
          height: height || (top || 0) + (bottom || 0) + GRAPH_NODE_SIZE,
        };

        // Add clone node to graph
        graph.add({
          group: 'nodes',
          data: {
            id: cloneId,
            laId: data.laId,
            actorId: data.actorId,
            isClone: true,
            privs: { modify: true },
            type: 'node',
            title: data.title || '',
            layerSettings: {
              ...layerSettings,
              ...layerSettingsSizes,
            },
          },
          position,
          grabbable: false,
        });

        cloneNodesRef.current[data.id] = cloneId;
      }
    }
  };

  /**
   * Remove a clone node when the original node is collapsed
   */
  const removeCloneNode = useCallback(
    (nodeId) => {
      if (!graph) return;

      const cloneId = cloneNodesRef.current[nodeId];

      // Remove the clone node if it exists
      if (cloneId) {
        const cloneNode = graph.$(`#${cloneId}`);
        if (!cloneNode.empty()) {
          cloneNode.remove();
        }
        delete cloneNodesRef.current[nodeId];
      }
    },
    [graph]
  );

  /**
   * Update layerSettings in clone nodes when original node layerSettings change
   * Only updates if offsets or relevant properties have changed
   */
  const updateCloneNodeSettings = useCallback(
    (node, newLayerSettings) => {
      if (!graph || !newLayerSettings) return;
      const data = node.data();

      const cloneId = cloneNodesRef.current[data.id];

      if (!cloneId) return;

      const cloneNode = graph.$(`#${cloneId}`);
      if (!cloneNode.empty()) {
        const currentSettings = cloneNode.data('layerSettings') || {};

        const newOffset = newLayerSettings.offset || {};
        const currentOffset = currentSettings.offset || {};

        const offsetChanged =
          newOffset?.left !== currentOffset?.left ||
          newOffset?.right !== currentOffset?.right ||
          newOffset?.top !== currentOffset?.top ||
          newOffset?.bottom !== currentOffset?.bottom;

        if (offsetChanged) {
          cloneNode.data('layerSettings', {
            ...newLayerSettings,
          });
        }

        // Update position
        const cloneNewPosition = calculateClonePosition(node, newLayerSettings);
        if (cloneNewPosition) {
          cloneNode.position(cloneNewPosition);
        }
      }
    },
    [graph]
  );

  // Initialise and clean up clone nodes
  useEffect(() => {
    if (!graph) return;

    const handleAllDataChanges = (event) => {
      const node = event.target;
      if (!node || !node.isNode || !node.isNode()) return;

      const data = node.data();
      const layerSettings = data.layerSettings || {};

      // Skip if the node is a clone or has other special properties
      if (!data || data.isClone || layerSettings.expand === undefined) return;

      const nodeId = data.id;

      if (layerSettings.expand) {
        // Check if this node has a clone
        const cloneId = cloneNodesRef.current[nodeId];
        if (!cloneId) {
          createCloneNodeInternal(node);
        } else {
          updateCloneNodeSettings(node, layerSettings);
        }
      } else {
        removeCloneNode(nodeId);
      }
    };

    // Listen for data changes (expansion/collapse and other changes)
    graph.on('data', handleAllDataChanges);

    return () => {
      // Remove event listener
      graph.off('data', handleAllDataChanges);

      // Remove clone nodes
      Object.entries(cloneNodesRef.current).forEach(([, cloneId]) => {
        const cloneNode = graph.$(`#${cloneId}`);
        if (cloneNode && !cloneNode.empty()) {
          cloneNode.remove();
        }
      });
      cloneNodesRef.current = {};
    };
  }, [graph, removeCloneNode, updateCloneNodeSettings]);

  return {
    // Return public API
    removeCloneNode,
    createCloneNode: createCloneNodeInternal,
  };
};

export default useCloneExpNodes;
